/**
 * Program Plans — service layer for the Program Model tool (room polygons over per-level plan
 * PDFs). Backed by api/program-plans.js (Supabase `program_plans` + `program_plan_rooms` +
 * the public `program-plans` bucket). See docs/PROGRAM_MODEL.md.
 *
 * Internal team tool only (Clerk-authed everywhere; no public/token path in v1).
 *
 * The Airtable side of the tool (Units/Levels/Rooms reads, and the `Area Manual` mirror writes)
 * goes through services/design.js — this file owns only the Supabase geometry.
 */

async function authHeaders() {
  const token = await window.__clerkGetToken()
  return { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token }
}

async function call(method, body, qs) {
  const res = await fetch('/api/program-plans' + (qs ? '?' + qs : ''), {
    method,
    headers: await authHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || ('Program plan request failed (' + res.status + ')'))
  return data
}

async function putSigned(signedUrl, body, contentType) {
  const res = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType, 'x-upsert': 'true' },
    body,
  })
  if (!res.ok) throw new Error('Upload failed (' + res.status + ')')
}

/**
 * Everything for one concept in one round trip.
 * -> { plansByLevel: { [levelId]: plan }, polysByRoom: { [roomId]: polygon },
 *      viewsByUnit: { [unitId]: view[] } }
 */
export async function getProgramModel(conceptId) {
  const { plans, polygons, views } = await call('GET', null, 'conceptId=' + encodeURIComponent(conceptId))
  const plansByLevel = {}
  for (const p of (plans || [])) plansByLevel[p.levelId] = p
  const polysByRoom = {}
  for (const g of (polygons || [])) polysByRoom[g.roomId] = g
  const viewsByUnit = {}
  for (const v of (views || [])) (viewsByUnit[v.unitId] || (viewsByUnit[v.unitId] = [])).push(v)
  return { plansByLevel, polysByRoom, viewsByUnit }
}

/**
 * Start a plan upload (first upload or replace). Returns { planId, priorPageFrame, pdfUpload }.
 * Nothing on the existing plan is touched until finalizePlanUpload — the caller needs
 * priorPageFrame to decide reprojection first.
 */
export function createPlanUploadUrl({ levelId, conceptId, projectId }) {
  return call('POST', { action: 'uploadUrl', levelId, conceptId, projectId })
}

/** PUT the extracted single-page PDF blob to the signed URL from createPlanUploadUrl. */
export function uploadPlanPdf(pdfUpload, blob) {
  return putSigned(pdfUpload.signedUrl, blob, 'application/pdf')
}

/**
 * Commit an uploaded plan PDF. `scalePoints`/`scalePointsPerFoot` keys: include them (even as
 * null) to overwrite, omit to leave the existing calibration untouched. `polygons` carries
 * reprojected points when the new page frame differs from the old one.
 */
export async function finalizePlanUpload({ planId, pdfPath, pageFrame, scalePoints, scalePointsPerFoot, polygons }) {
  const body = { action: 'finalizeUpload', planId, pdfPath, pageFrame }
  if (scalePoints !== undefined) body.scalePoints = scalePoints
  if (scalePointsPerFoot !== undefined) body.scalePointsPerFoot = scalePointsPerFoot
  if (polygons !== undefined) body.polygons = polygons
  const { plan } = await call('POST', body)
  return plan
}

/** Calibrate/recalibrate; `areas` updates every polygon's computed_sf in the same call. */
export async function setPlanScale({ planId, scalePoints, scalePointsPerFoot, areas }) {
  const { plan } = await call('POST', { action: 'setScale', planId, scalePoints, scalePointsPerFoot, areas })
  return plan
}

/**
 * Set (or clear, passing null) this level's one-time alignment anchor — a single shared
 * real-world reference point (e.g. a building corner) that lets a Unit View's callout position
 * (stored as a feet offset from the anchor) project correctly onto every level of the same unit.
 * See docs/PROGRAM_MODEL.md — "Unit Views."
 */
export async function setPlanAnchor({ planId, anchorPoint }) {
  const { plan } = await call('POST', { action: 'setAnchor', planId, anchorPoint })
  return plan
}

/**
 * Set (or clear, passing null) a level's STRUCTURAL finish elevation — e.g. top of slab/subfloor,
 * NOT the finished-floor elevation (the flooring material applied over the structural finish;
 * easy to confuse the two). Plain data-entry field, no downstream computation yet.
 */
export async function setLevelElevation({ planId, elevation }) {
  const { plan } = await call('POST', { action: 'setLevelElevation', planId, elevation })
  return plan
}

/** Upsert a room's polygon (one per room, keyed by the Airtable room id). */
export async function saveRoomPolygon({ planId, roomId, points, computedSf }) {
  const { polygon } = await call('POST', { action: 'savePolygon', planId, roomId, points, computedSf })
  return polygon
}

export function deleteRoomPolygon(roomId) {
  return call('POST', { action: 'deletePolygon', roomId })
}

export function deletePlan(planId) {
  return call('POST', { action: 'deletePlan', planId })
}
